import { ChangeEvent, ReactNode } from "react";
import styles from "./MinMaxSelects.module.scss";
import Select, { Option } from "../../base/Select/Select";

interface MinMaxSelectProps {
  groupLabel?: string;
  // Rendered at the right end of the group label's row (e.g. a Clear Filters pill).
  labelAction?: ReactNode;

  minLabel?: string;
  maxLabel?: string;

  minPlaceholder?: string;
  maxPlaceholder?: string;

  minValue?: number;
  maxValue?: number;

  options: Option[];
  onChange: (min?: number, max?: number) => void;
  className?: string;

  // Formats an applied value that isn't one of the fixed options (e.g. an
  // AI-search price like 825000) so it shows a clean label instead of blank.
  formatValue?: (n: number) => string;
}

export default function MinMaxSelect({
  groupLabel,
  labelAction,
  minLabel,
  maxLabel,
  minPlaceholder = "No Min.",
  maxPlaceholder = "No Max.",
  minValue,
  maxValue,
  options,
  onChange,
  className,
  formatValue,
}: MinMaxSelectProps) {
  const handleMinChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    onChange(value || undefined, maxValue);
  };

  const handleMaxChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    onChange(minValue, value || undefined);
  };

  // Insert an applied value that isn't already a fixed option, in sorted
  // position, so the select renders its label instead of falling back to blank.
  const withApplied = (opts: Option[], value?: number): Option[] => {
    if (!value || opts.some((o) => Number(o.value) === value)) return opts;
    const extra: Option = {
      value,
      label: formatValue ? formatValue(value) : value.toLocaleString(),
    };
    const merged = [...opts, extra];
    merged.sort((a, b) => Number(a.value) - Number(b.value));
    return merged;
  };

  const minOptions: Option[] = [
    { label: minPlaceholder, value: "" },
    ...withApplied(
      options.filter((o) => !maxValue || Number(o.value) <= maxValue),
      minValue
    ),
  ];

  const maxOptions: Option[] = [
    { label: maxPlaceholder, value: "" },
    ...withApplied(
      options.filter((o) => !minValue || Number(o.value) >= minValue),
      maxValue
    ),
  ];

  return (
    <div className={`${className} ${styles.minMaxSelect}`}>
      {(groupLabel || labelAction) && (
        <div className={styles.groupHeader}>
          {groupLabel && <p className={styles.groupLabel}>{groupLabel}</p>}
          {labelAction}
        </div>
      )}

      <div className={styles.selectsContainer}>
        <Select
          label={minLabel}
          name="min"
          value={minValue ?? ""}
          options={minOptions}
          onChange={handleMinChange}
        />

        <span>to</span>

        <Select
          label={maxLabel}
          name="max"
          value={maxValue ?? ""}
          options={maxOptions}
          onChange={handleMaxChange}
        />
      </div>
    </div>
  );
}
